/**
 * Gas cooker listing spec (Wave 2 gas cooker unified Phase 1).
 *
 * Canonical id: `gas-cooker` (new). Four legacy cooker ids are aliases for reads/backfill.
 * Required: `gas_cooker_mount_type`, `gas_cylinder_included`. Optional: `burner_count`.
 * `gas-cylinder` is a separate subcategory — never merged here (D4).
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const GAS_COOKER_MOUNT_TYPES = ["standing", "table_top"] as const;
export type GasCookerMountType = (typeof GAS_COOKER_MOUNT_TYPES)[number];

export const CANONICAL_GAS_COOKER_SUBCATEGORY_ID = "gas-cooker" as const;

export const LEGACY_GAS_COOKER_SUBCATEGORY_IDS = [
  "standing-gas-cooker",
  "table-top-gas-cooker",
  "standing-gas-cooker-cylinder",
  "table-top-gas-cooker-cylinder",
] as const;

export type LegacyGasCookerSubcategoryId =
  (typeof LEGACY_GAS_COOKER_SUBCATEGORY_IDS)[number];

export const GAS_COOKER_SUBCATEGORY_IDS = [
  CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
  ...LEGACY_GAS_COOKER_SUBCATEGORY_IDS,
] as const;

export type GasCookerSubcategoryId =
  (typeof GAS_COOKER_SUBCATEGORY_IDS)[number];

export const GAS_COOKER_BURNER_COUNT_MIN = 1;
export const GAS_COOKER_BURNER_COUNT_MAX = 6;

export type LegacyGasCookerSubcategoryMapping = {
  legacyId: string;
  canonicalId: typeof CANONICAL_GAS_COOKER_SUBCATEGORY_ID;
  gasCookerMountType: GasCookerMountType;
  gasCylinderIncluded: boolean;
};

/** Locked Phase 0 alias map (SIO + reference backfill). */
export const LEGACY_GAS_COOKER_SUBCATEGORY_MAP: readonly LegacyGasCookerSubcategoryMapping[] =
  [
    {
      legacyId: "standing-gas-cooker",
      canonicalId: CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
      gasCookerMountType: "standing",
      gasCylinderIncluded: false,
    },
    {
      legacyId: "table-top-gas-cooker",
      canonicalId: CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
      gasCookerMountType: "table_top",
      gasCylinderIncluded: false,
    },
    {
      legacyId: "standing-gas-cooker-cylinder",
      canonicalId: CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
      gasCookerMountType: "standing",
      gasCylinderIncluded: true,
    },
    {
      legacyId: "table-top-gas-cooker-cylinder",
      canonicalId: CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
      gasCookerMountType: "table_top",
      gasCylinderIncluded: true,
    },
  ] as const;

const mapByLegacyId = new Map(
  LEGACY_GAS_COOKER_SUBCATEGORY_MAP.map((row) => [row.legacyId, row]),
);

export type GasCookerSpecFormState = {
  gasCookerMountType: GasCookerMountType | "";
  gasCylinderIncluded: boolean | "";
  burnerCount: number | "";
};

export function defaultGasCookerSpecFormState(): GasCookerSpecFormState {
  return {
    gasCookerMountType: "",
    gasCylinderIncluded: "",
    burnerCount: "",
  };
}

export function isGasCookerMountType(
  value: unknown,
): value is GasCookerMountType {
  return (
    typeof value === "string" &&
    (GAS_COOKER_MOUNT_TYPES as readonly string[]).includes(value)
  );
}

export function isGasCylinderIncludedValue(value: unknown): value is boolean {
  return value === true || value === false;
}

export function isGasCookerSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is GasCookerSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (GAS_COOKER_SUBCATEGORY_IDS as readonly string[]).includes(subcategoryId)
  );
}

export function isCanonicalGasCookerSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_GAS_COOKER_SUBCATEGORY_ID;
}

export function getGasCookerSpecSectionLabel(): string {
  return "Gas cooker details";
}

export function resolveCanonicalGasCookerSubcategoryId(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;
  if (subcategoryId === CANONICAL_GAS_COOKER_SUBCATEGORY_ID) {
    return CANONICAL_GAS_COOKER_SUBCATEGORY_ID;
  }
  return mapByLegacyId.get(subcategoryId)?.canonicalId ?? null;
}

export function getLegacyGasCookerSubcategoryMapping(
  subcategoryId: string | null | undefined,
): LegacyGasCookerSubcategoryMapping | null {
  if (!subcategoryId) return null;
  return mapByLegacyId.get(subcategoryId) ?? null;
}

export function inferGasCookerAttrsFromLegacySubcategoryId(
  subcategoryId: string | null | undefined,
): {
  gasCookerMountType: GasCookerMountType;
  gasCylinderIncluded: boolean;
} | null {
  const mapped = getLegacyGasCookerSubcategoryMapping(subcategoryId);
  if (!mapped) return null;
  return {
    gasCookerMountType: mapped.gasCookerMountType,
    gasCylinderIncluded: mapped.gasCylinderIncluded,
  };
}

const COOKER_TITLE_SIGNAL =
  /cooker|burner|stove|oven|hot\s*plate|cook\s*top|cooking|\bface\b/i;

const CYLINDER_TITLE_SIGNAL =
  /cylinder|cyl\b|\d+\s*(?:\.\d+)?\s*(?:kg|kgs)\s*(?:gas\s*)?cyl/i;

/** Cylinder-only listings belong on `gas-cylinder`, not `gas-cooker` (D4/D8). */
export function isCylinderOnlyGasTitle(
  title: string | null | undefined,
): boolean {
  const t = String(title ?? "").toLowerCase();
  if (!t.trim()) return false;
  if (/cylinder\s*only|only\s*(?:a\s*)?cylinder/.test(t)) return true;
  if (!CYLINDER_TITLE_SIGNAL.test(t)) return false;
  if (COOKER_TITLE_SIGNAL.test(t)) return false;
  if (/^\s*\d+(?:\.\d+)?\s*(?:kg|kgs)?\s*gas\s*cylinder/.test(t)) return true;
  if (/^\s*\d+(?:\.\d+)?\s*(?:kg|kgs)\s*gas\s*cylinder\s*$/.test(t))
    return true;
  return false;
}

export function inferGasCookerMountTypeFromTitle(
  title: string | null | undefined,
): GasCookerMountType | null {
  const t = String(title ?? "").toLowerCase();
  if (/table[\s-]?top|tabletop|\btable top\b/.test(t)) return "table_top";
  if (/\bstanding\b/.test(t)) return "standing";
  return null;
}

export function inferGasCylinderIncludedFromTitle(
  title: string | null | undefined,
): boolean | null {
  const t = String(title ?? "").toLowerCase();
  if (isCylinderOnlyGasTitle(title)) return null;
  if (CYLINDER_TITLE_SIGNAL.test(t) && COOKER_TITLE_SIGNAL.test(t)) {
    return true;
  }
  if (/without\s*cylinder|no\s*cylinder|cooker\s*only/.test(t)) return false;
  return null;
}

/**
 * Resolve mount + cylinder from legacy id defaults, then title overrides.
 * Returns null when title is cylinder-only (D8) or id is not a cooker alias/canonical.
 */
export function resolveGasCookerAttrsForLegacySubcategory(
  subcategoryId: string | null | undefined,
  title?: string | null,
): {
  gasCookerMountType: GasCookerMountType;
  gasCylinderIncluded: boolean;
} | null {
  if (isCylinderOnlyGasTitle(title)) return null;

  let defaults = inferGasCookerAttrsFromLegacySubcategoryId(subcategoryId);
  if (!defaults && subcategoryId === CANONICAL_GAS_COOKER_SUBCATEGORY_ID) {
    defaults = {
      gasCookerMountType: "standing",
      gasCylinderIncluded: false,
    };
  }
  if (!defaults) return null;

  const mountFromTitle = inferGasCookerMountTypeFromTitle(title);
  const cylinderFromTitle = inferGasCylinderIncludedFromTitle(title);

  return {
    gasCookerMountType: mountFromTitle ?? defaults.gasCookerMountType,
    gasCylinderIncluded: cylinderFromTitle ?? defaults.gasCylinderIncluded,
  };
}

export function formatGasCookerMountTitleWord(
  mount: GasCookerMountType,
): string {
  return mount === "table_top" ? "Table-top" : "Standing";
}

export function formatBurnerCountTitlePrefix(
  burnerCount: number | undefined,
): string | undefined {
  if (
    typeof burnerCount !== "number" ||
    !Number.isFinite(burnerCount) ||
    burnerCount < GAS_COOKER_BURNER_COUNT_MIN
  ) {
    return undefined;
  }
  const n = Math.trunc(burnerCount);
  return `${n}-Burner`;
}

/**
 * Locked: burner? → mount → brand? → Gas Cooker → optional & Cylinder
 */
export function composeGasCookerTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const mount = attrs.gas_cooker_mount_type;
  const cylinderIncluded = attrs.gas_cylinder_included;
  if (
    !isGasCookerMountType(mount) ||
    !isGasCylinderIncludedValue(cylinderIncluded)
  ) {
    return "";
  }

  const burnerRaw = attrs.burner_count;
  const burnerPrefix =
    typeof burnerRaw === "number" && Number.isFinite(burnerRaw)
      ? formatBurnerCountTitlePrefix(burnerRaw)
      : undefined;

  return composeListingTitleFromSlotsWithIdentity(
    {
      capacity: burnerPrefix,
      form: formatGasCookerMountTitleWord(mount),
      product: "Gas Cooker",
      extras: cylinderIncluded ? "& Cylinder" : undefined,
    },
    identity,
  );
}

export function buildGasCookerAttributesFromForm(
  form: GasCookerSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (isGasCookerMountType(form.gasCookerMountType)) {
    out.gas_cooker_mount_type = form.gasCookerMountType;
  }
  if (isGasCylinderIncludedValue(form.gasCylinderIncluded)) {
    out.gas_cylinder_included = form.gasCylinderIncluded;
  }
  if (
    typeof form.burnerCount === "number" &&
    Number.isFinite(form.burnerCount)
  ) {
    out.burner_count = form.burnerCount;
  }
  return out;
}

export function getGasCookerSpecFormValidationMessage(
  form: GasCookerSpecFormState,
): string | undefined {
  const issues = validateStoredGasCookerAttributes(
    buildGasCookerAttributesFromForm(form),
  );
  return issues[0]?.message;
}

export type GasCookerFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

/** Hydrate sell/edit form from stored `items.attributes` (+ legacy alias fallback). */
export function gasCookerFormFromAttributes(
  attributes: unknown,
  options?: GasCookerFormHydrateOptions,
): GasCookerSpecFormState {
  const base = defaultGasCookerSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  let gasCookerMountType: GasCookerMountType | "" = isGasCookerMountType(
    attrs.gas_cooker_mount_type,
  )
    ? attrs.gas_cooker_mount_type
    : base.gasCookerMountType;
  let gasCylinderIncluded: boolean | "" = isGasCylinderIncludedValue(
    attrs.gas_cylinder_included,
  )
    ? attrs.gas_cylinder_included
    : base.gasCylinderIncluded;

  const burnerRaw = attrs.burner_count;
  let burnerCount: number | "" = base.burnerCount;
  if (typeof burnerRaw === "number" && Number.isFinite(burnerRaw)) {
    burnerCount = burnerRaw;
  }

  const legacyMap = getLegacyGasCookerSubcategoryMapping(
    options?.subcategoryId,
  );
  if (
    legacyMap &&
    (!gasCookerMountType || gasCylinderIncluded === "") &&
    !isCylinderOnlyGasTitle(options?.title)
  ) {
    const resolved = resolveGasCookerAttrsForLegacySubcategory(
      options?.subcategoryId,
      options?.title,
    );
    if (resolved) {
      if (!gasCookerMountType) gasCookerMountType = resolved.gasCookerMountType;
      if (gasCylinderIncluded === "") {
        gasCylinderIncluded = resolved.gasCylinderIncluded;
      }
    }
  }

  return {
    gasCookerMountType,
    gasCylinderIncluded,
    burnerCount,
  };
}

export function validateStoredGasCookerAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const mount = attrs.gas_cooker_mount_type;
  const cylinderIncluded = attrs.gas_cylinder_included;
  const burner = attrs.burner_count;

  if (mount === undefined || mount === null || mount === "") {
    issues.push({
      key: "gas_cooker_mount_type",
      code: "required",
      message: "Choose standing or table-top.",
    });
  } else if (!isGasCookerMountType(mount)) {
    issues.push({
      key: "gas_cooker_mount_type",
      code: "invalid_enum",
      message: "Mount type must be standing or table-top.",
    });
  }

  if (
    cylinderIncluded === undefined ||
    cylinderIncluded === null ||
    cylinderIncluded === ""
  ) {
    issues.push({
      key: "gas_cylinder_included",
      code: "required",
      message: "Say whether a gas cylinder is included.",
    });
  } else if (!isGasCylinderIncludedValue(cylinderIncluded)) {
    issues.push({
      key: "gas_cylinder_included",
      code: "invalid_type",
      message: "Cylinder included must be yes or no.",
    });
  }

  if (burner !== undefined && burner !== null && burner !== "") {
    if (typeof burner !== "number" || !Number.isFinite(burner)) {
      issues.push({
        key: "burner_count",
        code: "invalid_type",
        message: "Burner count must be a number.",
      });
    } else if (
      burner < GAS_COOKER_BURNER_COUNT_MIN ||
      burner > GAS_COOKER_BURNER_COUNT_MAX
    ) {
      issues.push({
        key: "burner_count",
        code: "out_of_range",
        message: `Burner count must be between ${GAS_COOKER_BURNER_COUNT_MIN} and ${GAS_COOKER_BURNER_COUNT_MAX}.`,
      });
    }
  }

  return issues;
}
